import { useState } from 'react';
import {
  RefreshCw,
  Phone,
  Users,
  Activity,
} from 'lucide-react';
import toast from 'react-hot-toast';

interface QuickActionsBarProps {
  onRefreshExtensions?: () => void;
  onRefreshQueues?: () => void;
  onRefreshCalls?: () => void;
  onRefreshAll?: () => void;
}

/** Refresh buttons for the dashboard page title: the first is filled, the rest outlined. */
export function QuickActionsBar({
  onRefreshExtensions,
  onRefreshQueues,
  onRefreshCalls,
  onRefreshAll,
}: QuickActionsBarProps) {
  const [refreshing, setRefreshing] = useState<string | null>(null);

  const handleRefresh = async (
    type: string,
    action?: () => void
  ) => {
    setRefreshing(type);
    try {
      if (action) {
        await action();
      }
      toast.success(`${type} refreshed`);
    } catch (error) {
      toast.error(`Failed to refresh ${type}`);
    } finally {
      setTimeout(() => setRefreshing(null), 500);
    }
  };

  const quickActions = [
    { id: 'all', label: 'Refresh All', icon: RefreshCw, action: onRefreshAll },
    { id: 'extensions', label: 'Extensions', icon: Phone, action: onRefreshExtensions },
    { id: 'queues', label: 'Queues', icon: Users, action: onRefreshQueues },
    { id: 'calls', label: 'Active Calls', icon: Activity, action: onRefreshCalls },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      {quickActions.map((action, index) => {
        const Icon = action.icon;
        const isRefreshing = refreshing === action.label;

        return (
          <button
            key={action.id}
            type="button"
            onClick={() => handleRefresh(action.label, action.action)}
            disabled={isRefreshing}
            title={action.label}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-60 sm:px-5 ${
              index === 0
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'border border-gray-300 text-gray-800 hover:border-gray-900 dark:border-gray-600 dark:text-gray-100 dark:hover:border-gray-300'
            }`}
          >
            <Icon size={16} className={isRefreshing ? 'animate-spin' : ''} />
            <span className={index === 0 ? '' : 'hidden sm:inline'}>{action.label}</span>
          </button>
        );
      })}
    </div>
  );
}
